// Kept in sync with helpers/consent.ts in walkthrough-v2-front.
declare global {
    interface Navigator {
        // Set by browsers and extensions that send the Global Privacy Control signal.
        globalPrivacyControl?: boolean;
    }

    interface Window {
        // Only the parts of Termly's public API we rely on. `getConsentState` returns the
        // stored choices, or the region's defaults when nothing has been chosen yet.
        Termly?: {
            getConsentState?: () => Record<string, boolean> | undefined;
            on?: (event: 'consent', handler: (payload: TermlyConsentEvent) => void) => void;
            off?: (event: 'consent', handler: (payload: TermlyConsentEvent) => void) => void;
        };
        // Termly exposes this while enable_widget is on; it opens the preferences modal.
        displayPreferenceModal?: () => void;
        // HubSpot's command queue. Its banner script reads this and its other five scripts
        // (analytics, ads pixels, collected forms, conversations) honour the result natively.
        _hsp?: unknown[][];
    }
}

export type TermlyConsentEvent = {categories: Record<string, boolean>};

/**
 * Termly's cookie categories. `essential` is always granted and never gates anything.
 */
export const ConsentCategory = {
    advertising: 'advertising',
    analytics: 'analytics',
    performance: 'performance',
} as const;

export type ConsentCategoryName = (typeof ConsentCategory)[keyof typeof ConsentCategory];

/**
 * Whether the visitor has opted out of the sale/sharing of their personal information.
 *
 * Termly's `do_not_sell` alone is not enough. Its default consent sets carry
 * `do_not_sell: false` under BOTH opt-in and opt-out, and when a browser sends Global
 * Privacy Control Termly switches the region to the opt-in defaults — honouring GPC for
 * blocking but never reflecting it in `do_not_sell`, and `getConsentState()` filters `gpc`
 * out of what it returns. So a GPC visitor who never opens the preferences modal would
 * read as sellable, which is exactly backwards: GPC is a binding opt-out of sale in CA,
 * CO, CT, TX and MT. Hence the explicit navigator check.
 */
export const isDoNotSellRequested = (): boolean => {
    try {
        if (window.navigator?.globalPrivacyControl === true) return true;
        return Boolean(window.Termly?.getConsentState?.()?.do_not_sell);
    } catch {
        return false;
    }
};

/**
 * Whether a category is currently granted. Before the visitor chooses, Termly returns the
 * region's defaults — under opt-in that is `essential` only, so everything here is false
 * and nothing non-essential may load.
 */
export const hasConsent = (category: ConsentCategoryName): boolean => {
    // Global Privacy Control is a binding opt-out of sale, sharing and cross-context
    // behavioural advertising in CA, CO, CT, TX and MT — so it overrides an Accept for the
    // advertising category. Termly stopped honouring GPC when `enable_do_not_sell` was
    // switched off (its signal is the conjunction of that flag and `enable_gpc`), so the app
    // enforces it directly rather than relying on the CMP.
    //
    // Scoped to advertising on purpose: GPC does not cover first-party analytics, and that
    // category already waits for explicit consent.
    if (category === ConsentCategory.advertising && isDoNotSellRequested()) return false;

    try {
        return Boolean(window.Termly?.getConsentState?.()?.[category]);
    } catch {
        // Termly not loaded, or storage blocked. Withholding consent is the safe default.
        return false;
    }
};

/**
 * Runs `handler` with the current state and again whenever the visitor changes it, so a
 * script can start late if consent arrives after page load. Returns an unsubscribe fn.
 *
 * `initial` separates the state the page loaded with from a decision the visitor made. Under
 * opt-in the loaded state is "nothing granted yet", which looks identical to a decline unless
 * the caller can tell them apart — and treating it as one would silence the pre-consent
 * funnel for the whole visit.
 */
export const onConsentChange = (handler: (event: {initial: boolean}) => void): (() => void) => {
    handler({initial: true});

    const listener = () => handler({initial: false});
    window.Termly?.on?.('consent', listener);

    return () => window.Termly?.off?.('consent', listener);
};

// Termly styles and disables elements carrying this class, so it is worth setting. It does
// NOT reliably wire the click: Termly scans once at init, and its fallback observer only
// inspects each added node itself, never its descendants — so a button mounted inside a
// client-rendered subtree is missed. Hence openCookiePreferences below.
export const TERMLY_PREFERENCES_CLASS = 'termly-display-preferences';

/**
 * Denies the Google Consent Mode advertising signals when GPC is present.
 *
 * Without this, honouring GPC only works outside production. In production the Meta Pixel is
 * served from GTM rather than the app (NEXT_PUBLIC_DISABLE_FACEBOOK_PIXEL), so the container
 * carries every advertising tag — and Termly derives its GCM values from its own consent
 * state, which says advertising is granted once the visitor accepts. That would re-grant
 * `ad_storage` over the top of a binding opt-out.
 *
 * Re-applied after every Termly consent change, since Termly pushes its own `consent update`
 * on each one and last write wins.
 */
export const denyAdvertisingSignalsForGpc = (): void => {
    if (!isDoNotSellRequested()) return;

    const dataLayer = (window.dataLayer = window.dataLayer || []);

    // Consent Mode reads the `arguments` object that gtag() pushes — a plain array is not
    // recognised — so this has to be a function declaration rather than an arrow.
    function pushConsentCommand(_command: 'consent', _action: 'update', _params: Record<string, string>) {
        // eslint-disable-next-line prefer-rest-params
        dataLayer.push(arguments);
    }

    pushConsentCommand('consent', 'update', {
        ad_personalization: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
    });
};

/** Opens Termly's preferences modal. No-op until Termly has finished initialising. */
export const openCookiePreferences = (): void => {
    window.displayPreferenceModal?.();
};

/**
 * Hands Termly's decision to HubSpot instead of blocking its script.
 *
 * `js.hs-scripts.com` is only a loader; it fans out to six independent scripts — analytics,
 * ads pixels, the cookie banner, collected forms, conversations and web interactives. Blocking
 * it would stop tracking but also kill lead-form capture and live chat, which are not
 * consent-gated. Instead the banner script exposes `setHubSpotConsent`, and HubSpot's other
 * scripts subscribe to it, so they gate their own cookies natively.
 *
 * HubSpot forces `necessary: true` itself and derives its aggregate `allowed` flag from the
 * three categories below, so we only map those.
 */
export const syncHubSpotConsent = (): void => {
    const queue = (window._hsp = window._hsp || []);

    queue.push([
        'setHubSpotConsent',
        {
            analytics: hasConsent(ConsentCategory.analytics),
            advertisement: hasConsent(ConsentCategory.advertising),
            functionality: hasConsent(ConsentCategory.performance),
        },
    ]);
};

/**
 * Clears HubSpot's tracking cookies (`__hstc`, `hubspotutk`, `__hssc`, `__hssrc`,
 * `messagesUtk`). Scripts already executed cannot be unloaded, so callers reload afterwards.
 */
export const revokeHubSpotConsent = (): void => {
    const queue = (window._hsp = window._hsp || []);

    queue.push(['revokeCookieConsent']);
};
